/**
 * SurveyModal — avaliação da ferramenta (proposta "Review" do Claude Design, 17/10/2026).
 * Abre 1.5s depois do boot quando o /me traz `survey: true`, sem onboarding pendente (nem aberto
 * nesta sessão).
 *
 *  - Nota de 0 a 10 ("quanto a ferramenta ajuda nas suas metas"). Escolhida a nota, aparecem a
 *    pergunta e as opções da faixa (0–6, 7–8, 9–10, ver reviewText.ts) e o comentário opcional.
 *  - Enviar → POST /app/survey { quality, complement, question: 'goals', reasons } → obrigado.
 *  - Nota 9 ou 10 de quem ainda não participa do Programa de recomendação: o obrigado traz o
 *    convite. "Quero indicar" abre o OptInDialog (motivo "survey"); "Agora não" avisa o backend,
 *    que manda uma vez o email que deixa o convite guardado.
 *  - Fechar sem responder guarda a data neste navegador e espera 7 dias para perguntar de novo.
 *
 * Movimento (23/10/2026): véu e diálogo (.ds-veu, .ds-dialogo) entram com .is-aberto e saem em
 * 140 ms (usePresenca mantém montado enquanto sai). Escolhida a nota, a pergunta e as opções da
 * faixa abrem espaço (.ds-altura, 200 ms) e entram com fade; o obrigado traz o ✓ com .ds-sucesso
 * e o convite (nota 9 ou 10) entra depois dele (.ds-entra). Enquanto sai, o diálogo continua
 * montado e com o foco: depois de fechar, nenhum botão vale de novo (Enter repetido não manda
 * outro "Agora não").
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBrand } from '../../hooks/useBrand';
import { usePresenca } from '../../hooks/usePresenca';
import { useReferralLater, useSurvey } from '../../api/useFeedback';
import { useSessionStore } from '../../stores/sessionStore';
import { useAmbassadorStore } from '../ambassador/ambassadorStore';
import { useOnboardingStore } from '../onboarding/onboardingStore';
import { useGuiaOcupado } from '../guia/guiaStore';
import { ONBOARDING_ENABLED } from '../../config/env';
import { emitActivity } from '../../lib/activity';
import { REASONS, bandOf, reasonPt, useReviewText, type Band } from './reviewText';
import './review.css';

const SNOOZE_KEY = 'survey_snoozed_at';
const SNOOZE_DAYS = 7;

function snoozed(): boolean {
  try {
    const at = Number(localStorage.getItem(SNOOZE_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < SNOOZE_DAYS * 864e5;
  } catch {
    return false;
  }
}

function snooze(): void {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now()));
  } catch {
    /* storage indisponível */
  }
}

export function SurveyModal(): JSX.Element | null {
  const { tx, reason } = useReviewText();
  const brand = useBrand();
  const session = useSessionStore((s) => s.session);
  const onboardingShown = useOnboardingStore((s) => s.shown);
  const survey = useSurvey();
  const later = useReferralLater();
  const openOptIn = useAmbassadorStore((s) => s.openOptIn);
  const [visible, setVisible] = useState(false);
  const [closed, setClosed] = useState(false);
  const [score, setScore] = useState<number | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [sent, setSent] = useState(false);
  const product = brand.appTitle;

  // Com o onboarding ligado, a avaliação espera o perfil ficar completo (e a próxima visita).
  const onboardingPending = ONBOARDING_ENABLED && Boolean(session?.aware || session?.profile);
  // Com o guia passo a passo (ou o convite dele) na tela, a avaliação espera a próxima visita.
  const guiaNaTela = useGuiaOcupado();
  const shouldShow =
    Boolean(session?.survey) && !onboardingPending && !onboardingShown && !closed && !guiaNaTela;

  useEffect(() => {
    if (!shouldShow || snoozed()) return;
    const timer = setTimeout(() => {
      emitActivity('survey.shown');
      setVisible(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, [shouldShow]);

  useEffect(() => {
    if (!visible || closed) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // Fechar desliga .is-aberto na hora; o diálogo continua montado enquanto sai.
  const view = usePresenca(shouldShow && visible);
  if (!view.montado) return null;

  const band: Band | null = score === null ? null : bandOf(score);
  const invite = sent && score !== null && score >= 9 && !session?.ambassador?.slug;

  function dismiss(): void {
    if (closed) return;
    if (!sent) {
      emitActivity('survey.snoozed');
      snooze();
    }
    setClosed(true);
  }

  function pickScore(n: number): void {
    if (band !== bandOf(n)) setPicked([]);
    setScore(n);
  }

  function toggle(id: string): void {
    setPicked((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));
  }

  function submit(): void {
    if (closed || score === null || !band || survey.isPending) return;
    const reasons = picked.map((id) => reasonPt(id));
    emitActivity('survey.answered', { score, comment: comment.trim().length > 0 });
    survey.mutate(
      { quality: score, complement: comment.trim(), question: 'goals', reasons },
      { onSuccess: () => setSent(true) },
    );
  }

  function joinNow(): void {
    if (closed) return;
    setClosed(true);
    openOptIn('survey');
  }

  function notNow(): void {
    if (closed) return;
    later.mutate();
    setClosed(true);
  }

  return createPortal(
    <div className={`rv-overlay ds-veu${view.aberto ? ' is-aberto' : ''}`} role="presentation">
      <div
        className={`rv-dialog ds-dialogo${view.aberto ? ' is-aberto' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rv-title"
      >
        <button
          type="button"
          className="rv-close ds-resposta"
          aria-label={tx('close')}
          onClick={dismiss}
        >
          <span className="material-symbols-rounded" aria-hidden="true">
            close
          </span>
        </button>

        {sent && score !== null ? (
          <>
            <div className="rv-thanks">
              <span
                className="rv-thanks__check material-symbols-rounded ds-sucesso"
                aria-hidden="true"
              >
                check
              </span>
              <h2 id="rv-title">{tx('thanks_title', { n: score })}</h2>
            </div>
            {invite ? (
              <div className="rv-invite ds-entra">
                <strong>
                  <span className="material-symbols-rounded" aria-hidden="true">
                    redeem
                  </span>
                  {tx('invite_title')}
                </strong>
                <p>{tx('invite_text', { product })}</p>
                <div className="rv-invite__actions">
                  <button type="button" className="rv-dark ds-resposta" onClick={joinNow}>
                    {tx('invite_yes')}
                  </button>
                  <button type="button" className="rv-text ds-resposta" onClick={notNow}>
                    {tx('invite_later')}
                  </button>
                </div>
              </div>
            ) : (
              <p className="rv-thanks__text">{tx('thanks_text', { product })}</p>
            )}
          </>
        ) : (
          <>
            <div className="rv-head">
              <span className="rv-eyebrow">
                <span className="material-symbols-rounded" aria-hidden="true">
                  chat_bubble
                </span>
                {tx('eyebrow')}
              </span>
              <h2 id="rv-title">{tx('question', { product })}</h2>
            </div>

            <div className="rv-scale">
              <div className="rv-scale__row" role="radiogroup" aria-labelledby="rv-title">
                {Array.from({ length: 11 }, (_, n) => (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={score === n}
                    className={score === n ? 'is-on' : undefined}
                    onClick={() => pickScore(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <div className="rv-scale__legend">
                <span>{tx('scale_low')}</span>
                <span>{tx('scale_high')}</span>
              </div>
            </div>

            {/* Fica montado (fechado) antes da nota, para abrir espaço com transição. */}
            <div className={`rv-altura ds-altura${band ? ' is-aberto' : ''}`}>
              <div className="ds-altura-conteudo">
                {band ? (
                  <div className="rv-more">
                    <p className="rv-ask">{tx(`ask_${band}`)}</p>
                    <div className="rv-chips">
                      {REASONS[band].map((id) => (
                        <button
                          key={id}
                          type="button"
                          aria-pressed={picked.includes(id)}
                          className={`rv-chip${picked.includes(id) ? ' is-on' : ''}`}
                          onClick={() => toggle(id)}
                        >
                          {reason(id)}
                        </button>
                      ))}
                    </div>
                    <label className="rv-comment">
                      <span>
                        {tx('more')} <em>{tx('optional')}</em>
                      </span>
                      <textarea
                        className="ds-campo"
                        value={comment}
                        maxLength={1000}
                        placeholder={tx('more_placeholder')}
                        onChange={(e) => setComment(e.target.value)}
                      />
                    </label>
                    {survey.isError ? (
                      <p className="rv-error" role="alert">
                        {tx('send_error')}
                      </p>
                    ) : null}
                    <div className="rv-actions">
                      <button
                        type="button"
                        className="rv-primary ds-resposta"
                        disabled={survey.isPending}
                        onClick={submit}
                      >
                        {survey.isPending ? tx('sending') : tx('send')}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
